import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, Stack, TextField, Button, Box, CircularProgress } from '@mui/material';
import { DialogProps } from '@toolpad/core/useDialogs';
import { useTranslation } from 'react-i18next';
import { Lesson } from '@/models/lesson';

export function LessonRenameDialog({ payload, open, onClose }: DialogProps<{ data?: { lesson: Lesson } }, { name: string } | null>) {
    const { t } = useTranslation();
    const [saving, setSaving] = React.useState(false);
    const [nameError, setNameError] = React.useState(false);
    const [nameErrorMessage, setNameErrorMessage] = React.useState('');

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (nameError) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const name = data.get('lesson-name') as string;

        setSaving(true);
        await onClose({
            name,
        });
        setSaving(false);
    };

    const validateInputs = () => {
        const name = document.getElementById('lesson-name') as HTMLInputElement;

        let isValid = true;

        if (!name.value || !/^.+$/.test(name.value)) {
            setNameError(true);
            setNameErrorMessage(t('common.form.invalidLesson'));
            isValid = false;
        } else {
            setNameError(false);
            setNameErrorMessage('');
        }

        return isValid;
    };

    return (
        <Dialog fullWidth open={open} onClose={() => onClose(null)} disableRestoreFocus>
            <Box component='form' onSubmit={handleSubmit} noValidate>
                <DialogTitle>{t('lesson.actions.renameLesson')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            slotProps={{
                                htmlInput: {
                                    maxLength: 128,
                                    minLength: 1,
                                },
                            }}
                            id='lesson-name'
                            name='lesson-name'
                            defaultValue={payload.data?.lesson.latestLessonDraft.lessonName}
                            label={t('lesson.label.lessonName')}
                            helperText={nameErrorMessage}
                            error={nameError}
                            color={nameError ? 'error' : 'primary'}
                            required
                            fullWidth
                            autoFocus
                        />
                    </Box>
                </DialogContent>
                <Stack direction='row' sx={{ justifyContent: 'flex-end', gap: 1, p: 1 }}>
                    <Button
                        onClick={async () => {
                            onClose(null);
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button loading={saving} variant='contained' type='submit' onClick={validateInputs} loadingIndicator={<CircularProgress size={16} />}>
                        {t('common.confirm')}
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
}
